import React, { FC, useMemo } from "react"
import { Charge } from "../types";


interface ChargeListProps {
  charges: Charge[];
}

const ChargeList: FC<ChargeListProps> = ({ charges }) => {



  if (charges.length < 1) {
    return (
      <ul className="column">
        <li>
          {"No Charges Listed"}
        </li>
      </ul>
    )
  }

  return (
    <ul className="column charges">
      {charges.map((charge, i) => (
        <ChargeListItem key={i} charge={charge} />
      ))}
    </ul>
  )
}

interface ChargeListItemProps {
  charge: Charge;
}

// Charge descriptions run to 147 characters. The detention center asked for a summary,
// so trim on a word boundary. The statute code leads the description, so it survives.
// The description owns its own line now, so it no longer shares width with the bail
// amount and warrant number — hence a higher limit than the one-line layout allowed.
const DESCRIPTION_LIMIT = 140

const summarize = (description: string | null) => {
  if (!description || description.length <= DESCRIPTION_LIMIT) { return description }
  const clipped = description.slice(0, DESCRIPTION_LIMIT)
  const lastSpace = clipped.lastIndexOf(" ")
  let trimmed = lastSpace > 0 ? clipped.slice(0, lastSpace) : clipped
  // These descriptions end in notes aimed at records staff, e.g. "(Excludes manufacturing
  // meth; see CDR Code 3776)". Cutting into one leaves an unclosed bracket, so drop it whole.
  const openedNote = trimmed.lastIndexOf("(")
  if (openedNote > 0 && trimmed.indexOf(")", openedNote) === -1) {
    trimmed = trimmed.slice(0, openedNote)
  }
  return `${trimmed.replace(/[,;:\s]+$/, "")}\u2026`
}

// Each charge is a description line and a status line. On desktop the status fields sit in
// aligned columns so bond can be scanned down the list; on phones they collapse to one
// quiet line. Bookings entered since the PremierOne cutover leave bail unset until a bond
// exists, and roughly 1% of charges have no warrant number, so any status field can be
// empty \u2014 the CSS uses :empty guards rather than omitting the fields, which would break the
// column alignment.
//
// The statute code is deliberately NOT styled separately from the description. Only charges
// that resolve against tblaw are composed as "CODE - text"; the 83% converted from the old
// system carry code and text jammed together ("16-25-0020 DOMESTIC VIOLENCE 2ND DEGREE")
// with no separator to split on. Highlighting the code on the minority that parse would
// read as a rendering fault, not a design.
const ChargeListItem: FC<ChargeListItemProps> = ({ charge }) => {
  const description = useMemo(() => summarize(charge.description), [charge.description])
  // Counts describe the charge itself, so they belong with the description, not the status.
  const counts = charge.counts > 1 ? ` (${charge.counts} charges)` : ''
  const disposition = charge.disposition === 'Uninitialized' ? '' : charge.disposition
  return (
    <li>
      <span className="charge-description">{description}{counts}</span>
      <span className="charge-details">
        <span className="d-status">{disposition}</span>
        <span className="d-bail">{charge.bailAmount}</span>
        <span className="d-warrant">{charge.warrantNumber}</span>
      </span>
    </li>
  )
}


export default ChargeList